import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import PurchaseInvoiceRegisterForm from '../components/PurchaseInvoiceRegisterForm';
import { useGetPurchaseInvoiceRegisterHandler } from '../dataHandlers/getPurchaseInvoiceRegisterHandler';
import { useUpdatePurchaseInvoiceRegisterHandler } from '../dataHandlers/updatePurchaseInvoiceRegisterHandler';
import DocumentSkeletonForm from '../../../components/DocumentSkeletonForm';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_PURCHASE_REGISTER } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function EditPurchaseInvoiceRegisterPage() {
  const { purchaseInvoiceRegister } = useTranslatedMessages();
  useDocumentTitle(purchaseInvoiceRegister?.pages?.edit_title ?? 'Edit Purchase Invoice Register Entry');
  const navigate = useNavigate();
  const { document_id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const { register, isLoading } = useGetPurchaseInvoiceRegisterHandler(document_id);

  useEffect(() => {
    if (loading) return;
    if (!hasEInvoicePermission(UPDATE_PURCHASE_REGISTER)) {
      showActionDeniedSnackbar();
      navigate('/purchase-invoice-register', { replace: true });
    }
  }, [loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  useEffect(() => {
    if (!isLoading && register && register.document_mode !== 'NON_E_INVOICE') {
      const documentId = register.uuid || register.id;
      if (register.document_mode === 'E_INVOICE') {
        navigate(`/purchases/documents/invoices/${documentId}`, { replace: true });
      } else {
        navigate('/purchase-invoice-register', { replace: true });
      }
    }
  }, [isLoading, register, navigate]);

  const onRegisterUpdated = () => {
    navigate('/purchase-invoice-register', { replace: true });
  };

  const { onSubmit, error, isPending } = useUpdatePurchaseInvoiceRegisterHandler({ onSuccess: onRegisterUpdated });

  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const onSaveForm = (data) => {
    setIsSubmitting(true);
    onSubmit({ id: document_id, payload: data });
  };

  const onCancel = () => {
    navigate('/purchase-invoice-register');
  };

  if (isLoading || !register) {
    return <DocumentSkeletonForm />;
  }

  if (register.document_mode !== 'NON_E_INVOICE') {
    return <DocumentSkeletonForm />;
  }

  return (
    <Stack direction="column" sx={{ mt: '-1rem' }}>
      <PurchaseInvoiceRegisterForm
        initialData={register}
        onSubmit={onSaveForm}
        onCancel={onCancel}
        apiErrors={error}
        isLoading={isPending || isSubmitting}
      />
    </Stack>
  );
}
